import MediumArticle, { MediumArticleDisplayVariant } from 'components/ArticleGrid/MediumArticle';
import ArticleRowCarousel from 'components/ArticleGrid/ArticleRowCarousel';
import ArticleGridPaginated from 'components/ArticleGrid/ArticleGridPaginated';
import { RelatedArticleProps } from './types';
import { Color } from 'libs/contentful/types';
import { cn } from 'utils/styles';
import { Colorways } from 'types/constants';

export const MediumArticleGridLayout = {
  Flex: 'flex',
  Carousel: 'carousel',
  Paginated: 'paginated',
} as const;
export type MediumArticleGridLayout = (typeof MediumArticleGridLayout)[keyof typeof MediumArticleGridLayout];

// When cycling is enabled, cards rotate through these colorways based on index.
// Only Primary and Inverse bases have defined cycles; others fall back to Primary.
const CARD_COLORWAY_CYCLES: Partial<Record<Colorways, Colorways[]>> = {
  [Colorways.Primary]: [Colorways.Secondary, Colorways.Tertiary, Colorways.Inverse],
  [Colorways.Inverse]: [Colorways.Primary, Colorways.Secondary, Colorways.Tertiary],
  [Colorways.Secondary]: [Colorways.ActionPrimary, Colorways.Tertiary, Colorways.Inverse],
};

// When cycling is disabled, every card gets the same static colorway.
// Only Primary->Secondary is mapped; all other bases fall back to Primary.
const CARD_COLORWAY_STATIC: Partial<Record<Colorways, Colorways>> = {
  [Colorways.Primary]: Colorways.Secondary,
};

interface MediumArticleGridProps {
  articles: RelatedArticleProps[];
  layout: MediumArticleGridLayout;
  displayVariant: MediumArticleDisplayVariant;
  color?: Color;
  showDates?: boolean;
  includeCategoryLinks?: boolean;
  shouldCycleColors?: boolean;
  articlesPerPage?: number;
}

// Resolves which colorway a card should use based on the parent section's base
// color, the card's position in the grid, and whether color cycling is active.
const getCardColorway = (baseColorway: Colorways, index: number, shouldCycle: boolean): Colorways => {
  // Static mode: all cards share one colorway (or fall back to Primary)
  if (!shouldCycle) return CARD_COLORWAY_STATIC[baseColorway] ?? Colorways.Primary;
  // Cycling mode: round-robin through the cycle array
  const cycle = CARD_COLORWAY_CYCLES[baseColorway];
  if (!cycle) return Colorways.Primary;
  return cycle[index % cycle.length];
};

const MediumArticleGrid = ({
  articles,
  layout,
  displayVariant,
  color = { name: Colorways.Primary, value: '' },
  showDates,
  includeCategoryLinks,
  shouldCycleColors = false,
  articlesPerPage,
}: MediumArticleGridProps) => {
  const isCard = displayVariant === MediumArticleDisplayVariant.Card;

  const getCardClassName = (index: number): string | undefined => {
    if (!isCard) return undefined;
    return cn('w-full h-74 sm:aspect-square sm:h-auto', index >= 2 && 'sm:hidden md:flex');
  };

  const articleElements = articles.map((article, i) => (
    <MediumArticle
      {...article}
      displayVariant={displayVariant}
      colorway={isCard ? getCardColorway(color.name, i, shouldCycleColors) : undefined}
      showDates={showDates}
      includeCategoryLinks={includeCategoryLinks}
      className={getCardClassName(i)}
      key={article.sys?.id || i}
    />
  ));

  if (layout === MediumArticleGridLayout.Flex) {
    if (isCard) {
      return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">{articleElements}</div>;
    }
    return <div className="flex flex-col sm:flex-row sm:items-stretch gap-4">{articleElements}</div>;
  }

  if (layout === MediumArticleGridLayout.Paginated) {
    return <ArticleGridPaginated articlesPerPage={articlesPerPage}>{articleElements}</ArticleGridPaginated>;
  }

  return (
    <ArticleRowCarousel color={color} totalItems={articles.length}>
      {articleElements}
    </ArticleRowCarousel>
  );
};

export default MediumArticleGrid;
